import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api'; // Import our custom API instance
import SubscriptionPromptModal from '../components/SubscriptionPromptModal';
import CookieBlockedDialog from '../components/CookieBlockedDialog';
import { sanitizeError, logError } from '../utils/errorHandler';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Subscription prompt modal state
  const [showSubscriptionPrompt, setShowSubscriptionPrompt] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isSubscriptionModalActive, setIsSubscriptionModalActive] = useState(false); // Global modal state

  // True when login succeeded on the server but the browser did not keep the
  // session cookie (typically third-party cookies blocked)
  const [cookieBlocked, setCookieBlocked] = useState(false);

  // Runs AFTER the user is logged in and never blocks login. Asks the server
  // several times, spread over a few seconds, whether it recognises the session.
  // A single "not logged in" is not enough (it can be a timing blip), so the
  // dialog is only shown when every check fails.
  //   - returns the check_login payload as soon as one check says logged in
  //   - returns null when every check says "not logged in" (cookie not kept)
  //   - returns undefined when a check itself fails (can't tell -> never warn)
  const SESSION_CHECK_DELAYS_MS = [0, 1500, 4000];
  const findSession = async () => {
    for (let attempt = 0; attempt < SESSION_CHECK_DELAYS_MS.length; attempt++) {
      if (SESSION_CHECK_DELAYS_MS[attempt] > 0) {
        await new Promise((resolve) => setTimeout(resolve, SESSION_CHECK_DELAYS_MS[attempt]));
      }
      try {
        const response = await api.get('/check_login');
        if (response.data?.logged_in === true) {
          return response.data;
        }
        logger.warn(
          `Session check ${attempt + 1}/${SESSION_CHECK_DELAYS_MS.length}: server says not logged in. ` +
          `Session keys seen by server: ${Object.keys(response.data?.session_data || {}).join(', ') || '(none)'}`
        );
      } catch (err) {
        logger.warn('Could not check session after login:', err);
        return undefined;
      }
    }
    return null;
  };

  const refreshTeam = async () => {
    try {
      const response = await api.getMyTeam();
      if (response?.success && response.team) {
        setTeam(response.team);
      } else {
        setTeam(null);
      }
    } catch (err) {
      logger.error('Error loading team:', err);
      setTeam(null);
    }
  };

  // Function to check subscription status
  const checkSubscriptionStatus = async (preventAutoModal = false) => {
    try {
      const response = await api.get('/api/subscription/status');
      if (response.data.success && response.data.data?.status) {
        const status = response.data.data.status;
        setSubscriptionStatus(status);
        
        // Only show modal automatically if not prevented (i.e., not from a refresh action)
        if (!preventAutoModal) {
          // Determine if we should show the subscription prompt
          let shouldShowPrompt = false;
          
          if (status.status === 'trial') {
            // Show for trials with less than 7 days remaining
            shouldShowPrompt = status.days_remaining <= 7;
          } else if (status.status === 'expired') {
            // Always show for expired subscriptions
            shouldShowPrompt = true;
          } else if (status.status === 'cancelled') {
            // Only show for cancelled subscriptions within 7 days of expiration
            shouldShowPrompt = status.days_remaining <= 7;
          } else if (!status.status || status.status === 'none') {
            // Show for users with no subscription
            shouldShowPrompt = true;
          }
          
          logger.log(`[AuthContext] Auto-modal check: status=${status.status}, shouldShow=${shouldShowPrompt}, days=${status.days_remaining}`);
          setShowSubscriptionPrompt(shouldShowPrompt);
          setIsSubscriptionModalActive(shouldShowPrompt); // Set global modal state
        } else {
          logger.log('[AuthContext] Skipping auto-modal due to preventAutoModal=true');
        }
      }
    } catch (error) {
      logger.error('Error checking subscription status:', error);
    }
  };

  // Function to refresh subscription data and trigger global update
  const refreshSubscriptionData = async () => {
    await checkSubscriptionStatus(true); // Prevent auto modal on refresh
    // Trigger custom event for other components to refresh
    window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
  };

  // Function to control global subscription modal state
  const setGlobalSubscriptionModal = (isOpen, source = 'unknown') => {
    logger.log(`Global subscription modal ${isOpen ? 'opened' : 'closed'} by: ${source}`);
    setIsSubscriptionModalActive(isOpen);
    if (!isOpen) {
      // Also close the AuthContext modal if it's open
      setShowSubscriptionPrompt(false);
    }
  };

  // Function to manually trigger subscription modal with specific status
  const triggerSubscriptionModal = (userStatus = null, source = 'manual') => {
    logger.log(`[AuthContext] triggerSubscriptionModal called by: ${source}, modalActive: ${isSubscriptionModalActive}`);
    if (userStatus) {
      logger.log(`[AuthContext] Setting userStatus:`, userStatus);
      setSubscriptionStatus(userStatus);
    }
    setShowSubscriptionPrompt(true);
    setIsSubscriptionModalActive(true);
  };

  useEffect(() => {
    if (user) {
      refreshTeam();
    } else {
      setTeam(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Check if user is already logged in
  useEffect(() => {
    // Detect the marker once and remove it from the URL so a reload doesn't repeat it
    const urlParams = new URLSearchParams(window.location.search);
    const microsoftLoginJustCompleted = urlParams.get('ms_login') === 'success';
    if (urlParams.has('ms_login')) {
      urlParams.delete('ms_login');
      const query = urlParams.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
    }

    const checkLoginStatus = async () => {
      try {
        setLoading(true);
        
        const response = await api.get('/check_login');
        
        if (response.data.logged_in) {
          logger.log("User is logged in according to server");
          setUser(response.data.user);
          // Store admin status in localStorage
          localStorage.setItem('isAdmin', response.data.user.is_admin);
          
          // Check subscription status for logged in user
          await checkSubscriptionStatus();
        } else {
          logger.log("User is not logged in");
          localStorage.removeItem('isAdmin');
          setUser(null);
          setTeam(null);

          // The backend adds ms_login=success when it has just logged the user in
          // via Microsoft. Re-check a few times; if the server still doesn't
          // recognise the session the browser did not keep the cookie.
          if (microsoftLoginJustCompleted) {
            findSession().then((session) => {
              if (session === null) {
                logger.error('Microsoft login completed, but the server does not recognise the session (cookie not kept by the browser?)');
                setCookieBlocked(true);
              } else if (session?.logged_in) {
                // It was only a timing blip: the user IS logged in, so let them in
                setUser(session.user);
                localStorage.setItem('isAdmin', session.user.is_admin);
                checkSubscriptionStatus();
              }
            });
          }
        }
      } catch (err) {
        logError(err, 'AuthContext.checkLoginStatus');
        setError(sanitizeError(err, 'Failed to check login status'));
        setUser(null);
        localStorage.removeItem('isAdmin');
      } finally {
        setLoading(false);
      }
    };

    checkLoginStatus();
  }, []);

  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/login', { username, password });
      
      if (response.data.success) {
        // Set user state immediately
        setUser(response.data.user);
        
        // Store admin status in localStorage
        localStorage.setItem('isAdmin', response.data.user.is_admin);
        
        logger.log(`User ${username} successfully logged in`);

        // In the background, check that the browser really keeps the session.
        // This never blocks or undoes the login; it only explains the problem
        // if the server keeps not recognising the session.
        findSession().then((session) => {
          if (session === null) {
            logger.error('Logged in, but the server does not recognise the session (cookie not kept by the browser?)');
            setCookieBlocked(true);
          }
        });
        
        // Check subscription status after successful login
        await checkSubscriptionStatus();
        
        return { success: true, user: response.data.user };
      } else {
        setError(response.data.message || 'Login failed');
        return { success: false, message: response.data.message };
      }
    } catch (err) {
      logError(err, 'AuthContext.login');
      const errorMessage = sanitizeError(err, 'Login failed. Please try again.');
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async (forceNewLogin = false) => {
    try {
      setLoading(true);
      
      // Send logout request with forceNewLogin parameter
      await api.post('/logout', { forceNewLogin });
      
      // Clear user state
      setUser(null);
      setTeam(null);
      
      // Clear all authentication data
      localStorage.removeItem('isAdmin');
      
      // Clear Microsoft-related tokens from localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('msal.') || key.includes('microsoft')) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear Microsoft-related tokens from sessionStorage
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('msal.') || key.includes('microsoft') || key.includes('ms_redirect')) {
          sessionStorage.removeItem(key);
        }
      });
      
      // If force new login is needed, redirect to login page with special parameter
      if (forceNewLogin) {
        // Add a timestamp to prevent caching
        window.location.href = `/login?force_new_login=true&t=${Date.now()}`;
        return;
      }
    } catch (err) {
      logger.error('Error during logout:', err);
      setError('Failed to logout');
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      logger.log('Refreshing user data...');
      const response = await api.get('/check_login');
      
      if (response.data.logged_in) {
        logger.log('User data refreshed successfully');
        setUser(response.data.user);
        // Update admin status in localStorage
        localStorage.setItem('isAdmin', response.data.user.is_admin);
        
        // Check subscription status after refresh
        await refreshSubscriptionData();
        
        return { success: true, user: response.data.user };
      } else {
        logger.log('User is no longer logged in');
        localStorage.removeItem('isAdmin');
        setUser(null);
        setShowSubscriptionPrompt(false); // Hide prompt if user logged out
        return { success: false, message: 'User is no longer logged in' };
      }
    } catch (err) {
      logger.error('Error refreshing user data:', err);
      setError('Failed to refresh user data');
      return { success: false, message: 'Failed to refresh user data' };
    }
  };

  // Handle subscription prompt modal close
  const handleSubscriptionPromptClose = (subscribed) => {
    logger.log(`[AuthContext] Modal closed, subscribed: ${subscribed}`);
    setShowSubscriptionPrompt(false);
    setIsSubscriptionModalActive(false);
    
    if (subscribed) {
      // Refresh subscription status after subscription
      logger.log('[AuthContext] Refreshing subscription status after subscription');
      checkSubscriptionStatus(true); // Prevent auto-modal on refresh
    }
  };

  // Admin/Supervisor function to get all users
  const getUsers = async () => {
    if (!user?.is_admin && user?.role_type !== 'Supervisor') {
      return { success: false, message: 'Admin or Supervisor access required' };
    }
    
    try {
      const response = await api.get('/api/admin/users');
      return { success: true, users: response.data.users };
    } catch (err) {
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to fetch users'
      };
    }
  };
  
  // Admin/Supervisor function to get user login history
  const getUserLoginHistory = async (username) => {
    if (!user?.is_admin && user?.role_type !== 'Supervisor') {
      return { success: false, message: 'Admin or Supervisor access required' };
    }
    
    try {
      const response = await api.get(`/api/admin/login-history${username ? `?username=${username}` : ''}`);
      return { 
        success: true, 
        logins: response.data.logins,
        total: response.data.total
      };
    } catch (err) {
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to fetch login history'
      };
    }
  };
  
  // Admin/Supervisor function to get company research logs
  const getCompanyResearchLogs = async (filters = {}) => {
    if (!user?.is_admin && user?.role_type !== 'Supervisor') {
      return { success: false, message: 'Admin or Supervisor access required' };
    }
    
    try {
      // Build query string from filters
      const queryParams = new URLSearchParams();
      if (filters.username) queryParams.append('username', filters.username);
      if (filters.company) queryParams.append('company', filters.company);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.success !== undefined) queryParams.append('success', filters.success);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.per_page) queryParams.append('per_page', filters.per_page);
      
      const queryString = queryParams.toString();
      const response = await api.get(`/api/admin/company-research-logs${queryString ? `?${queryString}` : ''}`);
      
      return { 
        success: true, 
        logs: response.data.logs,
        total: response.data.total,
        pages: response.data.pages,
        current_page: response.data.current_page
      };
    } catch (err) {
      logger.error('Error fetching company research logs:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to fetch company research logs'
      };
    }
  };
  
  // Admin-only function to create a new user
  const createUser = async (userData) => {
    if (!user?.is_admin) {
      return { success: false, message: 'Admin access required' };
    }
    
    try {
      const response = await api.post('/api/admin/users', userData);
      return { 
        success: true, 
        user: response.data.user,
        message: response.data.message
      };
    } catch (err) {
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to create user'
      };
    }
  };
  
  // Admin-only function to update a user
  const updateUser = async (username, userData) => {
    if (!user?.is_admin) {
      return { success: false, message: 'Admin access required' };
    }
    
    try {
      logger.log('Sending update for user:', username);
      logger.log('Update data being sent:', JSON.stringify(userData));
      
      const response = await api.put(`/api/admin/users/${username}`, userData);
      logger.log('Server response:', response.data);
      
      return { 
        success: true, 
        user: response.data.user,
        message: response.data.message
      };
    } catch (err) {
      logger.error('Update user error:', err);
      logger.error('Response data:', err.response?.data);
      
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to update user'
      };
    }
  };
  
  // Admin-only function to delete a user
  const deleteUser = async (username) => {
    if (!user?.is_admin) {
      return { success: false, message: 'Admin access required' };
    }
    
    try {
      const response = await api.delete(`/api/admin/users/${username}`);
      return { 
        success: true, 
        message: response.data.message
      };
    } catch (err) {
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to delete user'
      };
    }
  };

  const getUserDeletePreview = async (username) => {
    if (!user?.is_admin) {
      return { success: false, message: 'Admin access required' };
    }

    try {
      const response = await api.get(`/api/admin/users/${username}/delete-preview`);
      return {
        success: true,
        preview: response.data.preview
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to load delete preview'
      };
    }
  };
  
  // Admin-only function to get claiming statistics
  const getClaimStats = async () => {
    if (!user?.is_admin) {
      return { success: false, message: 'Admin access required' };
    }
    
    try {
      logger.log("Fetching claim statistics...");
      const response = await api.get('/api/admin/claim-stats');
      logger.log("Claim stats API response:", response.data);
      
      // Make sure the response is properly formatted
      if (response.data && response.data.success) {
        return { 
          success: true, 
          stats: response.data.stats,
          claims_by_user: response.data.claims_by_user,
          claim_details: response.data.claim_details
        };
      } else {
        logger.error("Invalid claim stats response format:", response.data);
        return { 
          success: false, 
          message: response.data?.message || 'Invalid response format from server'
        };
      }
    } catch (err) {
      logger.error("Error fetching claim statistics:", err);
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to fetch claim statistics'
      };
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    isSupervisor: user?.role_type === 'Supervisor' || false,
    team,
    refreshTeam,
    subscriptionStatus,
    showSubscriptionPrompt,
    setShowSubscriptionPrompt,
    checkSubscriptionStatus,
    refreshSubscriptionData,
    // Global subscription modal management
    isSubscriptionModalActive,
    setGlobalSubscriptionModal,
    // Admin functions
    getUsers,
    getUserLoginHistory,
    getCompanyResearchLogs,
    createUser,
    updateUser,
    deleteUser,
    getUserDeletePreview,
    getClaimStats,
    refreshUser,
    // New function
    triggerSubscriptionModal
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* AuthContext controls the global modal when it's shown */}
      <SubscriptionPromptModal
        open={showSubscriptionPrompt}
        onClose={handleSubscriptionPromptClose}
        userStatus={subscriptionStatus}
      />
      <CookieBlockedDialog
        open={cookieBlocked}
        onClose={() => setCookieBlocked(false)}
      />
    </AuthContext.Provider>
  );
}; 